import { DISCIPLINES } from '../../core/contracts'
import { buildInsights } from '../../core/insights'
import type { SessionSummary } from '../../core/insights'
import { scoreValue } from '../../core/scoring'
import { useT } from '../../platform/i18n/useT'
import type { TKey } from '../../platform/i18n/i18nContext'
import { GAMES } from '../../registry'
import type { Progress } from '../useProgress'
import './screens.css'

interface Props {
  progress: Progress
  streak: number
  sessions: readonly SessionSummary[]
  onDone: () => void
}

/**
 * Итог тренировки дня.
 *
 * Показывается один раз после последнего упражнения. Смысл экрана -
 * закрыть цикл: человек видит, что программа пройдена, как изменился
 * счёт и что серия выросла. Без него последний ответ просто выбрасывал
 * обратно на главный.
 */
export function DaySummary({ progress, streak, sessions, onDone }: Props) {
  const { t } = useT()
  const { total, byDiscipline, weekChange } = progress

  const references = Object.fromEntries(GAMES.map((g) => [g.id, g.referenceRtMs]))
  const insights = buildInsights(sessions, references)

  return (
    <div className="ds">
      <div className="ds__em">{t('home.workout')}</div>
      <h2 className="ds__h">{t('today.doneTitle')}</h2>

      <div className="ds__score">
        {scoreValue(total) ?? '—'}
        <span>{t('index.title')}</span>
      </div>

      {total.status === 'collecting' && scoreValue(total) !== null && (
        <p className="ds__note">{t('index.provisional', { n: total.sessionsLeft })}</p>
      )}

      {weekChange !== null && (
        <p className="ds__delta">
          {t('index.weekChange', { n: weekChange > 0 ? `+${weekChange}` : weekChange })}
        </p>
      )}

      <div className="ds__grid">
        {DISCIPLINES.map((d) => {
          const s = byDiscipline[d]
          return (
            <div key={d} className="ds__c">
              <div className="ds__k">{t(`discipline.${d}`)}</div>
              <div className="ds__v">{scoreValue(s) ?? '—'}</div>
            </div>
          )
        })}
      </div>

      {insights.length > 0 && (
        <div className="ds__insights">
          <div className="ds__label">{t('insight.title')}</div>
          {insights.map((i) => (
            <div key={i.titleKey} className={`ds__ins ds__ins--${i.tone}`}>
              <b>
                {t(
                  i.titleKey as TKey,
                  i.params
                    ? { ...i.params, d: t(`discipline.${i.params['d']}` as TKey) }
                    : undefined,
                )}
              </b>
              <span>{t(i.adviceKey as TKey, i.params)}</span>
            </div>
          ))}
        </div>
      )}

      {streak > 0 && <p className="ds__streak">{t('common.streak', { n: streak })}</p>}

      <button type="button" className="ds__btn" onClick={onDone}>
        {t('result.finish')}
      </button>
    </div>
  )
}
